/** @format */

import React, { createContext, useEffect, useReducer } from 'react';
import Keycloak from 'keycloak-js';
import SplashScreen from 'src/components/SplashScreen';
import { keycloakConfig } from 'src/config';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import { useTranslation } from 'react-i18next';
import { setUserData } from 'src/slices/users';
import { useDispatch } from 'src/store';
import { applyUserLocaleToI18n } from 'src/i18n';
import { getLocaleFromKeycloakClient } from 'src/utils/keycloakLocale';
import { clearPersistedAcceptLanguage, getPersistedAcceptLanguage, setPersistedAcceptLanguage } from 'src/utils/acceptLanguage';

export let keycloakClient = new Keycloak({
  ...keycloakConfig
});

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
  role: null,
  error: null,
  email: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { user, uuid, isAuthenticated } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
        uuid
      };
    }
    case 'LOGIN': {
      const { user, role, user_status, uuid, email } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        isInitialised: true,
        user,
        user_status,
        role: role,
        uuid: uuid,
        email
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'ON_ERROR': {
      return {
        ...state,
        isInitialised: true,
        isAuthenticated: false,
        user: null,
        error: action.payload.error
      };
    }
    case 'UPDATE_ONBOARDING_COMPLETE': {
      return {
        ...state,
        user: state.user ? { ...state.user, onBoardingComplete: true } : state.user
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const storeDispatch = useDispatch();

  const {
    i18n: { language: locale }
  } = useTranslation();

  const logout = () => {
    clearPersistedAcceptLanguage();
    keycloakClient.logout();
  };

  const syncI18nFromKeycloak = async () => {
    const persisted = getPersistedAcceptLanguage();
    if (persisted) {
      await applyUserLocaleToI18n(persisted);
      return;
    }
    const fromToken = getLocaleFromKeycloakClient(keycloakClient);
    if (fromToken) {
      setPersistedAcceptLanguage(fromToken);
      await applyUserLocaleToI18n(fromToken);
    }
  };

  const getUserInfo = async (roles, uuid, email) => {
    const response = await axios.get(`${ENDPOINTS.GET_USER_INFO_ENDPOINT}`);
    const user = response.data;
    const onboardingComplete = user.onboardingComplete ?? false;
    const userWithDefaults = {
      ...user,
      email,
      onBoardingComplete: onboardingComplete
    };
    dispatch({
      type: 'LOGIN',
      payload: {
        user: userWithDefaults,
        role: roles[0],
        uuid
      }
    });
    storeDispatch(setUserData(userWithDefaults));
  };

  const updateOnBoardingComplete = () => {
    dispatch({ type: 'UPDATE_ONBOARDING_COMPLETE' });
  };

  useEffect(() => {
    const initialise = async () => {
      try {
        keycloakClient.onTokenExpired = async () => {
          const refreshed = await keycloakClient.updateToken();
          if (refreshed) {
            axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
            await syncI18nFromKeycloak();
          }
        };
        keycloakClient.onAuthRefreshError = () => {
          window.location.href = '/';
        };
        keycloakClient.onAuthError = () => {
          window.location.href = '/';
        };

        keycloakClient.onAuthSuccess = () => {};
        keycloakClient.onReady = () => {};
        keycloakClient.onAuthLogout = () => {};
        keycloakClient.onAuthRefreshSuccess = () => {
          void syncI18nFromKeycloak();
        };

        await keycloakClient.init({
          onLoad: 'login-required',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          redirectUri: window.location.href,
          pkceMethod: 'S256',
          locale: locale || 'en'
        });

        if (keycloakClient.authenticated) {
          axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
          await syncI18nFromKeycloak();
          const keyClockAccessRoles = keycloakClient.resourceAccess[keycloakClient.clientId].roles || [];
          getUserInfo(keyClockAccessRoles, keycloakClient.subject, keycloakClient.tokenParsed.email);
        }
      } catch (err) {
        dispatch({
          type: 'ON_ERROR',
          payload: {
            isAuthenticated: false,
            user: null,
            error: {
              message: 'Error while trying to log in, please try again',
              err
            }
          }
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        logout,
        updateOnBoardingComplete
      }}
    >
      {state.isAuthenticated ? children : <></>}
    </AuthContext.Provider>
  );
};

export default AuthContext;
